import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { notificationService } from "../../services/notificationService";
import type { SystemNotification, NewSystemNotification } from "../../types";

export const useNotifications = () => {
	const { user } = useAuth();
	const [notifications, setNotifications] = useState<SystemNotification[]>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(true);

	// Load notifications
	const loadNotifications = useCallback(async () => {
		if (!user) {
			setNotifications([]);
			setUnreadCount(0);
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			const [userNotifications, count] = await Promise.all([
				notificationService.getUserNotifications(user.uid),
				notificationService.getUnreadCount(user.uid),
			]);
			setNotifications(userNotifications);
			setUnreadCount(count);
		} catch (error) {
			console.error("Error loading notifications:", error);
		} finally {
			setLoading(false);
		}
	}, [user]);

	// Load notifications on mount and when user changes
	useEffect(() => {
		loadNotifications();
	}, [loadNotifications]);

	// Mark notification as read
	const markAsRead = useCallback(async (notificationId: string) => {
		try {
			await notificationService.markAsRead(notificationId);
			setNotifications((prev) =>
				prev.map((notif) =>
					notif.id === notificationId ? { ...notif, isRead: true } : notif
				)
			);
			setUnreadCount((prev) => Math.max(0, prev - 1));
			return true;
		} catch (error) {
			console.error("Error marking notification as read:", error);
			return false;
		}
	}, []);

	// Dismiss notification
	const dismiss = useCallback(async (notificationId: string) => {
		try {
			await notificationService.deleteNotification(notificationId);
			setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
			setUnreadCount((prev) => Math.max(0, prev - 1));
			return true;
		} catch (error) {
			console.error("Error dismissing notification:", error);
			return false;
		}
	}, []);

	// Mark all notifications as read
	const markAllAsRead = useCallback(async () => {
		try {
			const unreadNotifications = notifications.filter((n) => !n.isRead);
			await Promise.all(
				unreadNotifications.map((n) => notificationService.markAsRead(n.id))
			);
			setNotifications((prev) =>
				prev.map((notif) => ({ ...notif, isRead: true }))
			);
			setUnreadCount(0);
			return true;
		} catch (error) {
			console.error("Error marking all notifications as read:", error);
			return false;
		}
	}, [notifications]);

	// Create new notification
	const createNotification = useCallback(
		async (notification: NewSystemNotification) => {
			if (!user) return null;

			try {
				const notificationId = await notificationService.createNotification({
					...notification,
					userId: user.uid,
				});

				// Reload notifications to get the updated list
				await loadNotifications();

				return notificationId;
			} catch (error) {
				console.error("Error creating notification:", error);
				return null;
			}
		},
		[user, loadNotifications]
	);

	return {
		notifications,
		unreadCount,
		loading,
		markAsRead,
		dismiss,
		markAllAsRead,
		createNotification,
		loadNotifications,
	};
};
